import { Button, Loader } from '@pwc/pwc-deals-ui';
import { xor } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import {
  getEditMode,
  getResetDivestitureDiagnosticResultsStatus,
  getSaveCategoriesStatus,
  resetDivestitureDiagnosticResults,
  saveCategories,
  fetchDivestitureDiagnosticResults,
  resetSaveCategoriesStatus,
  getFetchDivestitureDiagnosticResultsStatus,
  setEditMode,
  saveOverallComplexity,
  getSaveOverallComplexityStatus,
  resetSaveOverallComplexityStatus,
  getDivestitureDiagnosticResults,
} from '../../../features/divestitureDiagnostic/divestitureDiagnosticSlice';
import useUserReadability from '../../hooks/useUserReadability.ts';
import DealComplexityOverview from './DealComplexityOverview/DealComplexityOverview';
import ResultsHeader from './ResultsHeader/ResultsHeader';
import TopLevelCategoryOverview from './TopLevelCategoryOverview/TopLevelCategoryOverview';

import './DivestitureDiagnosticResults.scss';
import { resetPostResponsesStatus } from '../../../Survey/surveySlice';
import validateCategoryOnChange from './DivestitureDiagnosticUtils/validateCategoryOnChange';
import featureFlags from '../../helpers/featureFlags';

const matchCategories = (previousCats, newCats) =>
  xor(
    previousCats.map((cat) => cat.categoryId),
    newCats.map((cat) => cat.categoryId)
  ).length === 0;

const filterSubCategories = (categories, selectedCategory) =>
  categories.filter((cat) => cat.parentId === selectedCategory.categoryId);

const DivestitureDiagnosticResults = ({ category }) => {
  const [saveDisabled, setSaveDisabled] = useState(true);
  const dispatch = useDispatch();
  const { pending } = useSelector(getResetDivestitureDiagnosticResultsStatus);
  const { projectId } = useParams();
  const isUserReadonly = useUserReadability(projectId);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const divestitureDiagnosticResults = useSelector(
    getDivestitureDiagnosticResults
  );

  const [editableSelectedCategory, setEditableSelectedCategory] =
    useState(category);

  const editMode = useSelector(getEditMode);
  const { pending: isFetchDDResultsPending } = useSelector(
    getFetchDivestitureDiagnosticResultsStatus
  );
  const {
    pending: isSaveCategoriesPending,
    success: isSaveCategoriesSuccessful,
  } = useSelector(getSaveCategoriesStatus);
  const { pending: isSaveOverallPending, success: isSaveOverallSuccessful } =
    useSelector(getSaveOverallComplexityStatus);
  const isSavePending = isSaveCategoriesPending || isSaveOverallPending;
  const isSaveSuccessful =
    isSaveCategoriesSuccessful || isSaveOverallSuccessful;

  const [subcategories, setSubcategories] = useState(
    divestitureDiagnosticResults
      ? filterSubCategories(divestitureDiagnosticResults.data, category)
      : []
  );

  useEffect(() => {
    if (divestitureDiagnosticResults && !editMode) {
      setEditableSelectedCategory(category);
      setSubcategories(
        filterSubCategories(divestitureDiagnosticResults.data, category)
      );
    }
  }, [category, divestitureDiagnosticResults, editMode]);

  useEffect(() => {
    const originalSubcategories = filterSubCategories(
      (divestitureDiagnosticResults && divestitureDiagnosticResults.data) || [],
      category
    );
    if (editMode && matchCategories(originalSubcategories, subcategories)) {
      const isSavable = validateCategoryOnChange(
        category,
        editableSelectedCategory,
        originalSubcategories,
        subcategories
      );
      setSaveDisabled(!isSavable);
    }
  }, [
    subcategories,
    category,
    divestitureDiagnosticResults,
    editMode,
    editableSelectedCategory,
  ]);

  useEffect(() => {
    if (isSaveSuccessful) {
      dispatch(resetSaveOverallComplexityStatus());
      dispatch(resetSaveCategoriesStatus());
      dispatch(setEditMode(false));
      dispatch(fetchDivestitureDiagnosticResults(projectId));
    }
  }, [isSaveSuccessful, dispatch, projectId]);

  useEffect(() => {
    dispatch(setEditMode(false));
  }, [category, dispatch]);

  const updateCategory = (editedCategoryId, update) => {
    const categoryToEdit = [...subcategories, editableSelectedCategory].find(
      (cat) => cat.categoryId === editedCategoryId
    );
    const updatedCategory = {
      ...categoryToEdit,
      ...update,
    };

    if (updatedCategory.categoryId === editableSelectedCategory.categoryId) {
      setEditableSelectedCategory(updatedCategory);
    } else {
      const updatedSubcategories = subcategories.map((cat) =>
        cat.categoryId === editedCategoryId ? updatedCategory : cat
      );
      setSubcategories(updatedSubcategories);
    }
  };

  const handleOnSave = () => {
    if (
      category.categoryId ===
      divestitureDiagnosticResults.overallDealComplexity.categoryId
    ) {
      dispatch(saveOverallComplexity(projectId, editableSelectedCategory));
    } else {
      dispatch(
        saveCategories(projectId, [...subcategories, editableSelectedCategory])
      );
    }
  };

  const overallDealComplexity =
    divestitureDiagnosticResults &&
    divestitureDiagnosticResults.overallDealComplexity;

  return (
    <>
      {divestitureDiagnosticResults && editableSelectedCategory && (
        <div className="dd-results-wrapper">
          {isPwcUser && (
            <ResultsHeader
              saveDisabled={saveDisabled || isSavePending}
              editDisabled={isFetchDDResultsPending || isUserReadonly}
              cancelDisabled={isSavePending}
              onSave={handleOnSave}
            />
          )}
          {(isSavePending || isFetchDDResultsPending) && <Loader />}
          {!isSavePending && !isFetchDDResultsPending && (
            <div className="divestiture-diagnostic-category-wrapper">
              {category.categoryId === overallDealComplexity.categoryId && (
                <DealComplexityOverview
                  selectedCategory={editableSelectedCategory}
                  categories={divestitureDiagnosticResults.data}
                  onChange={updateCategory}
                />
              )}
              {category.categoryId !== overallDealComplexity.categoryId && (
                <TopLevelCategoryOverview
                  onChange={updateCategory}
                  selectedCategory={editableSelectedCategory}
                  subcategories={subcategories}
                />
              )}
              {featureFlags.diagnosticDebuggingControls && !isUserReadonly && (
                <div className="dd-reset-results-btn">
                  <Button
                    className="reset-dd-results-button"
                    onClick={() => {
                      dispatch(resetPostResponsesStatus());
                      dispatch(resetDivestitureDiagnosticResults(projectId));
                    }}
                    disabled={pending}
                  >
                    Reset Divestiture Diagnostic Results
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DivestitureDiagnosticResults;
